// Libraries
import { calculateHistoricalStatistics } from "@/utils/calculateStatistics"; // Adjust path as needed
import { Configuration, GeographyApi } from "@esyoil-gmbh/geography-client";
import axios, { AxiosResponse } from "axios";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import utc from "dayjs/plugin/utc";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { BasePathType } from "~/mixins/breadcrumb";
import { replaceToUmlauts } from "~/mixins/mixins";
import { encodePlaceNameSegment } from "~/operations";
import {
  PelletsChartDataState,
  PelletsHistoryChartPayload,
} from "~/types/Charts";
import {
  CityLink,
  CommunityLink,
  CountyLink,
  CityPriceModel,
  DistrictCity,
  DistrictCityResponse,
  DistrictCityZipcode,
  FetchDistrictCitiesPayload,
  GroupedCommunity,
  OilState,
  OilTopCitiesState,
} from "~/types/Location";
import {
  PelletsPayload,
  PelletsState,
  PelletsTopCitiesData,
} from "~/types/Pellets";
import { PartnerType } from "~/types/Partner";
import {
  buildAlignedPelletsChartState,
  clonePelletsChartState,
  coercePelletsChartMonths,
  PelletsChartMonths,
} from "~/utils/pelletsChart";
import { fetchPelletsChartWithCache } from "~/utils/pelletsChartFetch";
import { pricePayloadToProductPrice } from "~/utils/priceUtils";

dayjs.extend(utc);
dayjs.extend(duration);

const config = new Configuration({
  basePath: process.env.BACKBONE_ESYOIL,
});
const api = new GeographyApi(config);

@Module({
  name: "federalstates",
  namespaced: true,
  stateFactory: true,
})
export default class Federalstates extends VuexModule {
  // Default state objects
  defaultOilState: OilState = {
    name: "",
    currentPrice: { value: 0, changedAtUnix: "" },
    lastPrice: { value: 0, changedAtUnix: "" },
    relativeChange: 0,
  };

  defaultPelletsState: PelletsState = {
    loose: {
      currentPrice: { value: 0, changedAtUnix: "" },
      lastPrice: { value: 0, changedAtUnix: "" },
      relativeChange: 0,
    },
    pallet: {
      currentPrice: { value: 0, changedAtUnix: "" },
      lastPrice: { value: 0, changedAtUnix: "" },
      relativeChange: 0,
    },
    bigbag: {
      currentPrice: { value: 0, changedAtUnix: "" },
      lastPrice: { value: 0, changedAtUnix: "" },
      relativeChange: 0,
    },
  };

  defaultPelletsTopCities: PelletsTopCitiesData = {
    loose: [],
    pallet: [],
    bigbag: [],
  };

  // State properties
  stateAssetsPrice: OilState = { ...this.defaultOilState };
  pelletsStateAssetsPrice: PelletsState = { ...this.defaultPelletsState };
  pelletsCountyAssetsPrice: PelletsState = { ...this.defaultPelletsState };
  pelletsTopCities: PelletsTopCitiesData = { ...this.defaultPelletsTopCities };

  city = {
    name: "",
    zipcode: null,
    district: null,
  };
  districtCities = [];
  districtCityZipcodes = [];
  statesOilPriceHistory = {
    reletadUnits: "€ / 100 L",
    labels: [],
    sateOilPriceData: [],
    nationalOiPriceData: [],
    countyOiPriceData: [],
  };

  countyNutsCode = null;
  countyName = null;

  countyYearStatistics = {
    highest: {
      fourWeeks: { price: 0, date: "" },
      threeMonths: { price: 0, date: "" },
      oneYear: { price: 0, date: "" },
    },
    lowest: {
      fourWeeks: { price: Infinity, date: "" },
      threeMonths: { price: Infinity, date: "" },
      oneYear: { price: Infinity, date: "" },
    },
  };

  stateYearStatistics = {
    highest: {
      fourWeeks: { price: 0, date: "" },
      threeMonths: { price: 0, date: "" },
      oneYear: { price: 0, date: "" },
    },
    lowest: {
      fourWeeks: { price: Infinity, date: "" },
      threeMonths: { price: Infinity, date: "" },
      oneYear: { price: Infinity, date: "" },
    },
  };

  stateArticle = {
    imageUrl: "",
    intro: [],
    content: [],
  };

  pelletsStateArticle = {
    imageUrl: "",
    intro: "",
    content: [],
  };

  historyTableData = [];
  topStateUserComments = [];
  topStatePartners = [];
  countyLinks: CountyLink[] = [];
  cityLinks: CityLink[][] = [];
  communityLinks: CommunityLink[] = [];
  bigCityLinks: CityLink[][] = [];
  oilTopCities: OilTopCitiesState = {
    oil: [],
  };
  historicalPriceData = {};
  cityPriceHistory = {
    chart: {
      reletadUnits: "€ / 100 L",
      labels: [],
      data: [],
    },
  };

  pelletsPriceHistory = {
    loose: {
      relatedUnits: "€ / t",
      labels: [],
      data: [],
    },
    pallet: {
      relatedUnits: "€ / t",
      labels: [],
      data: [],
    },
  };

  pelletsChartData: PelletsChartDataState = {
    labels: [],
    loose: {
      data: [],
    },
    pallet: {
      data: [],
    },
  };

  pelletsChartDataCache: Record<string, PelletsChartDataState> = {};

  // Tracks the most recent in-flight chart fetch per action so late responses
  // for an earlier range/region can be discarded (and not poison the cache).
  // Separate slots prevent a county fetch from clobbering a state fetch's key.
  pendingPelletsStateChartKey: string | null = null;
  pendingPelletsCountyChartKey: string | null = null;

  otherZipcodes: string[] = [];

  cityRequestData = {
    zipcode: "",
  };

  @Mutation
  clearCurrentStateAssetsPrice() {
    this.stateAssetsPrice = { ...this.defaultOilState };
  }

  @Mutation
  clearPelletsStateAssetsPrice() {
    this.pelletsStateAssetsPrice = { ...this.defaultPelletsState };
  }

  @Mutation
  clearPelletsCountyAssetsPrice() {
    this.pelletsCountyAssetsPrice = { ...this.defaultPelletsState };
  }

  @Mutation
  setCurrentStateAssetsPrice(assetsData) {
    this.stateAssetsPrice.name = "Heizöl";
    this.stateAssetsPrice.currentPrice.value = assetsData.currentPrice.value;
    this.stateAssetsPrice.currentPrice.changedAtUnix =
      assetsData.currentPrice.modifiedAt;
    this.stateAssetsPrice.lastPrice.value = assetsData.lastPrice.value;
    this.stateAssetsPrice.lastPrice.changedAtUnix =
      assetsData.lastPrice.modifiedAt;
    this.stateAssetsPrice.relativeChange = assetsData.relativeChange.toFixed(2);
  }

  @Mutation
  setCurrentPelletsStateAssetsPrice(payload: PelletsPayload) {
    if (payload.loose) {
      this.pelletsStateAssetsPrice.loose = pricePayloadToProductPrice(
        payload.loose,
      );
    }

    if (payload.pallet) {
      this.pelletsStateAssetsPrice.pallet = pricePayloadToProductPrice(
        payload.pallet,
      );
    }

    if (payload.bigbag) {
      this.pelletsStateAssetsPrice.bigbag = pricePayloadToProductPrice(
        payload.bigbag,
      );
    }
  }

  @Mutation
  setCurrentPelletsCountyAssetsPrice(payload: PelletsPayload) {
    if (payload.loose) {
      this.pelletsCountyAssetsPrice.loose = pricePayloadToProductPrice(
        payload.loose,
      );
    }

    if (payload.pallet) {
      this.pelletsCountyAssetsPrice.pallet = pricePayloadToProductPrice(
        payload.pallet,
      );
    }

    if (payload.bigbag) {
      this.pelletsCountyAssetsPrice.bigbag = pricePayloadToProductPrice(
        payload.bigbag,
      );
    }
  }

  @Mutation
  clearCountyLinks() {
    this.countyLinks = [];
  }

  @Mutation
  setCountyLinks(assetsData) {
    assetsData.forEach((item) => {
      this.countyLinks.push(item);
    });
  }

  @Mutation
  setCommunityLinks(assetsData) {
    assetsData.forEach((item) => {
      this.communityLinks.push(item);
    });
  }

  @Mutation
  setPelletsStateArticle(assetsData) {
    this.pelletsStateArticle.imageUrl = assetsData.image;
    this.pelletsStateArticle.intro = assetsData.intro;
    this.pelletsStateArticle.content = assetsData.content;
  }

  @Mutation
  clearCommunityLinks() {
    this.communityLinks = [];
  }

  @Mutation
  clearCityLinks() {
    this.cityLinks = [];
  }

  @Mutation
  clearPelletsChartData() {
    this.pelletsChartData = {
      labels: [],
      loose: { data: [] },
      pallet: { data: [] },
    };
  }

  @Mutation
  clearPelletsStateArticle() {
    this.pelletsStateArticle.imageUrl = "";
    this.pelletsStateArticle.intro = "";
    this.pelletsStateArticle.content = [];
  }

  @Mutation
  setCityLinks(assetsData) {
    for (let i = 0; i < 32; i++) {
      this.cityLinks.push([]);
    }
    assetsData.forEach((item) => {
      const SortIndex = item.ORT_NAME[0].toLowerCase().charCodeAt(0) - 97;
      this.cityLinks[SortIndex].push(item);
    });
  }

  @Mutation
  clearOilTopCities() {
    this.oilTopCities = {
      oil: [],
    };
  }

  @Mutation
  clearPelletsTopCities() {
    this.pelletsTopCities = { ...this.defaultPelletsTopCities };
  }

  @Mutation
  setOilTopCities(data: CityPriceModel[]) {
    this.oilTopCities.oil = data;
  }

  @Mutation
  setPelletsTopCities(data: PelletsTopCitiesData) {
    this.pelletsTopCities = {
      loose: data.loose || [],
      pallet: data.pallet || [],
      bigbag: data.bigbag || [],
    };
  }

  @Mutation
  clearStateArticle() {
    this.stateArticle.imageUrl = "";
    this.stateArticle.intro = [];
    this.stateArticle.content = [];
  }

  @Mutation
  setStateArticle(assetsData) {
    this.stateArticle.imageUrl = assetsData.image;
    this.stateArticle.intro = assetsData.intro;
    this.stateArticle.content = assetsData.content;
  }

  @Mutation
  clearStateAssets() {
    this.statesOilPriceHistory.labels = [];
    this.statesOilPriceHistory.sateOilPriceData = [];
  }

  @Mutation
  clearNationalAssets() {
    this.statesOilPriceHistory.nationalOiPriceData = [];
  }

  @Mutation
  clearPartners() {
    this.topStatePartners = [];
  }

  @Mutation
  clearUserComments() {
    this.topStateUserComments = [];
  }

  @Mutation
  restructureAndPusTopPartners(commentsData) {
    commentsData.forEach((item) => {
      this.topStatePartners.push(item);
    });
  }

  @Mutation
  setHistoryTableData(
    tableData: Array<{
      amount: number;
      delta: number;
      priceToday: number;
      priceYesterday: number;
    }>,
  ) {
    this.historyTableData = [];
    tableData.forEach((item) => {
      const tableRowData = [
        `${item.amount.toLocaleString("de-DE", { style: "decimal" })} Liter`,
        item.priceToday.toLocaleString("de-DE", {
          style: "currency",
          currency: "EUR",
        }),
        item.priceYesterday.toLocaleString("de-DE", {
          style: "currency",
          currency: "EUR",
        }),
        `${item.delta.toLocaleString("de-DE", {
          style: "decimal",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} %`,
      ];
      this.historyTableData.push(tableRowData);
    });
  }

  @Mutation
  restructureAndPushTopComments(commentsData) {
    commentsData.forEach((item, i) => {
      const Element = {
        _id: `comment-${i}`,
        customerStatement: item.rop_review_body,
        esyoilStatement: "",
        partnerStatement: "",
        updatedAtByCustomer: item.rop_date_created,
        businessCustomerId: "",
        rating: item.rop_rating_value,
        bcFullName: "",
        customerStatementLength: 0,
      };
      this.topStateUserComments.push(Element);
    });
  }

  @Mutation
  clearAllStateAssets() {
    this.statesOilPriceHistory.labels = [];
    this.statesOilPriceHistory.sateOilPriceData = [];
    this.statesOilPriceHistory.nationalOiPriceData = [];
    this.statesOilPriceHistory.countyOiPriceData = [];
  }

  @Mutation
  clearStateYearAssets() {
    this.stateYearStatistics = {
      highest: {
        fourWeeks: { price: 0, date: "" },
        threeMonths: { price: 0, date: "" },
        oneYear: { price: 0, date: "" },
      },
      lowest: {
        fourWeeks: { price: Infinity, date: "" },
        threeMonths: { price: Infinity, date: "" },
        oneYear: { price: Infinity, date: "" },
      },
    };
  }

  @Mutation
  clearCountyYearAssets() {
    this.countyYearStatistics = {
      highest: {
        fourWeeks: { price: 0, date: "" },
        threeMonths: { price: 0, date: "" },
        oneYear: { price: 0, date: "" },
      },
      lowest: {
        fourWeeks: { price: Infinity, date: "" },
        threeMonths: { price: Infinity, date: "" },
        oneYear: { price: Infinity, date: "" },
      },
    };
  }

  @Mutation
  setCityData(cityData: {
    zipcode: string | number;
    name: string;
    district: string;
  }) {
    this.city.name = cityData.name;
    this.city.zipcode = cityData.zipcode;
    this.city.district = cityData.district;
  }

  @Mutation
  restructureAndPushStateAssets(
    assetsData: {
      priceData: Array<string | number>;
      variant: string;
      addLabels: boolean;
    }[],
  ) {
    assetsData.forEach((assetData) => {
      assetData.priceData.forEach((item) => {
        const Timestamp = dayjs.unix(item[0]).utc().format("DD.MM.YYYY");
        const Price = item[1];
        if (assetData.addLabels === true)
          this.statesOilPriceHistory.labels.push(Timestamp);
        if (assetData.variant === "state")
          this.statesOilPriceHistory.sateOilPriceData.push(Price);
        if (assetData.variant === "nation")
          this.statesOilPriceHistory.nationalOiPriceData.push(Price);
        if (assetData.variant === "county")
          this.statesOilPriceHistory.countyOiPriceData.push(Price);
      });
    });
  }
  @Mutation
  setStateYearAssets(assetsData: []) {
    this.stateYearStatistics = calculateHistoricalStatistics(assetsData) as any;
  }

  @Mutation
  setCountyYearAssets(assetsData: []) {
    this.countyYearStatistics = calculateHistoricalStatistics(
      assetsData,
    ) as any;
  }

  @Mutation
  setDistrictCities(payload: {
    cities: DistrictCity[];
    currentDistrictCity?: string;
    shouldFilter?: boolean;
  }): void {
    if (payload.shouldFilter && payload.currentDistrictCity) {
      this.districtCities = payload.cities.filter(
        (city) => city.name !== payload.currentDistrictCity,
      );
    } else {
      this.districtCities = payload.cities;
    }
  }

  @Mutation
  setDistrictCityOtherZipcodes(districtZipcodes: DistrictCityZipcode[]): void {
    this.districtCityZipcodes = districtZipcodes;
  }

  @Mutation
  setSingleCityZipcode(zipcode: string) {
    this.cityRequestData.zipcode = zipcode;
  }

  @Mutation
  resetCityRequestData() {
    this.cityRequestData = {
      zipcode: "",
    };
  }

  @Mutation
  async setBigCityLinks(assetsData) {
    this.bigCityLinks = [];
    for (let i = 0; i < 32; i++) {
      this.bigCityLinks.push([]);
    }
    assetsData.forEach((item) => {
      const sortIndex = item.name.toLowerCase().charCodeAt(0) - 97;
      this.bigCityLinks[sortIndex].push(item);
    });
  }

  @Mutation
  setCountyInfo(payload: { nutsCode: string; name: string }): void {
    this.countyNutsCode = payload.nutsCode;
    this.countyName = payload.name;
  }

  @Mutation
  resetCountyInfo(): void {
    this.countyNutsCode = null;
    this.countyName = null;
  }

  @Mutation
  clearHistoricalPriceData() {
    this.cityPriceHistory.chart.labels = [];
    this.cityPriceHistory.chart.data = [];
  }

  @Mutation
  clearPelletsHistoricalPriceData() {
    this.pelletsPriceHistory = {
      loose: {
        relatedUnits: "€ / t",
        labels: [],
        data: [],
      },
      pallet: {
        relatedUnits: "€ / t",
        labels: [],
        data: [],
      },
    };
  }

  @Mutation
  setPendingPelletsStateChartKey(key: string | null) {
    this.pendingPelletsStateChartKey = key;
  }

  @Mutation
  setPendingPelletsCountyChartKey(key: string | null) {
    this.pendingPelletsCountyChartKey = key;
  }

  @Mutation
  setPelletsChartData(payload: {
    cacheKey: string;
    data: PelletsHistoryChartPayload;
  }): void {
    const next: PelletsChartDataState = buildAlignedPelletsChartState(
      payload.data,
    );

    // Cache only client-side so SSR workers stay stateless across requests.
    if (process.client) {
      this.pelletsChartDataCache = {
        ...this.pelletsChartDataCache,
        [payload.cacheKey]: next,
      };
    }
    this.pelletsChartData = clonePelletsChartState(next);
  }

  @Mutation
  loadCachedPelletsChartData(cacheKey: string) {
    const cached = this.pelletsChartDataCache[cacheKey];
    if (cached) this.pelletsChartData = clonePelletsChartState(cached);
  }

  @Mutation
  setOtherZipcodes(zipcodes) {
    this.otherZipcodes = zipcodes;
  }

  @Mutation
  setHistoricalPriceData(historicalPriceData) {
    historicalPriceData.forEach((element) => {
      this.cityPriceHistory.chart.data.push({
        x: element.date,
        y: Number(element.price),
      });
      this.cityPriceHistory.chart.labels.push(
        dayjs(element.date).format("DD.MM.YYYY"),
      );
    });
  }

  @Mutation
  setPelletHistoricalPriceData(historicalPriceData) {
    // Process the data for loose and pallet prices directly
    historicalPriceData.forEach((element) => {
      if (element.price_loose_6000 > 0) {
        this.pelletsPriceHistory.loose.data.push({
          x: element.date,
          y: Number(element.price_loose_6000),
        });
        this.pelletsPriceHistory.loose.labels.push(
          dayjs(element.date).format("DD.MM.YYYY"),
        );
      }
      if (element.price_pallet_6000 > 0) {
        this.pelletsPriceHistory.pallet.data.push({
          x: element.date,
          y: Number(element.price_pallet_6000),
        });
        this.pelletsPriceHistory.pallet.labels.push(
          dayjs(element.date).format("DD.MM.YYYY"),
        );
      }
    });
  }

  @Mutation
  restructurePushPartnersAndRatings(data) {
    this.topStatePartners = data.dealerList.sort(
      (a, b) => b.pre_grade - a.pre_grade,
    );
  }

  @Action
  async loadNationAndStateData(payload: { months: number; nutsCode: string }) {
    try {
      this.context.commit("clearAllStateAssets");
      const [nation, state] = await Promise.all([
        axios.get(
          `${process.env.API_ESYOIL}${process.env.CHARTS_JAHRESUEBERSICHT}/${payload.months}`,
        ),
        axios.get(
          `${process.env.API_ESYOIL}${process.env.FEDERAL_STATE_CHART}/${payload.nutsCode}/${payload.months}`,
        ),
      ]);

      this.context.commit("restructureAndPushStateAssets", [
        {
          priceData: nation.data.data.data,
          variant: "nation",
          addLabels: false,
        },
        {
          priceData: state.data.data.data,
          variant: "state",
          addLabels: true,
        },
      ]);
    } catch (e) {
      console.error(e);
    }
  }

  @Action
  async fetchPelletsStateChartData(payload: {
    nutsCode: string;
    monthsQuantity: PelletsChartMonths;
  }) {
    const months = coercePelletsChartMonths(payload.monthsQuantity);
    const cacheKey = `state:${payload.nutsCode}:${months}`;
    await fetchPelletsChartWithCache(this.context, {
      url: `${process.env.API_ESYOIL}${process.env.FEDERAL_STATE_CHART}/${payload.nutsCode}/${months}?type=PARTNER_PELLET`,
      cacheKey,
      getCached: () => this.pelletsChartDataCache[cacheKey],
      getPendingKey: () => this.pendingPelletsStateChartKey,
      setPendingMutation: "setPendingPelletsStateChartKey",
      loadCachedMutation: "loadCachedPelletsChartData",
      setDataMutation: "setPelletsChartData",
      logScope: "federalstates/fetchPelletsStateChartData",
    });
  }

  @Action
  async loadNationStateAndCountyData(payload: {
    months: number;
    stateNutsCode: string;
    countyNutsCode: string;
  }) {
    try {
      this.context.commit("clearAllStateAssets");
      const [nation, state, county] = await Promise.all([
        axios.get(
          `${process.env.API_ESYOIL}${process.env.CHARTS_JAHRESUEBERSICHT}/${payload.months}`,
        ),
        axios.get(
          `${process.env.API_ESYOIL}${process.env.FEDERAL_STATE_CHART}/${payload.stateNutsCode}/${payload.months}`,
        ),
        axios.get(
          `${process.env.API_ESYOIL}${process.env.COUNTY_CHART}/${payload.countyNutsCode}/${payload.months}`,
        ),
      ]);

      this.context.commit("restructureAndPushStateAssets", [
        {
          priceData: nation.data.data.data,
          variant: "nation",
          addLabels: false,
        },
        {
          priceData: state.data.data.data,
          variant: "state",
          addLabels: true,
        },
        {
          priceData: county.data.data.data,
          variant: "county",
          addLabels: false,
        },
      ]);
    } catch (e) {
      console.error(e);
    }
  }

  @Action
  async fetchStateYearData(payload: { nutsCode: string }) {
    try {
      this.context.commit("clearStateYearAssets");

      const state = await axios.get(
        `${process.env.API_ESYOIL}${process.env.FEDERAL_STATE_CHART}/${payload.nutsCode}/12`,
      );

      this.context.commit("setStateYearAssets", state.data.data.data);
    } catch (e) {
      console.error(e);
    }
  }

  @Action
  async fetchCountyYearData(payload: { countyNutsCode: string }) {
    try {
      this.context.commit("clearCountyYearAssets");
      const county = await axios.get(
        `${process.env.API_ESYOIL}${process.env.COUNTY_CHART}/${payload.countyNutsCode}/12`,
      );

      this.context.commit("setCountyYearAssets", county.data.data.data);
    } catch (e) {
      console.error(e);
    }
  }

  @Action
  async fetchCityName(payload: {
    zipcode: string;
    location: string | null;
    basePath: BasePathType;
  }) {
    try {
      const res: any = await api.geographyV1GetPlaceNamesZipcodeGet(
        payload.zipcode,
      );

      if (res.data.length === 0) {
        return;
      }

      if (res.data && res.data.length > 0) {
        const normalizeLocation = (str: string): string =>
          replaceToUmlauts(
            decodeURIComponent(str).replaceAll("|", "/"),
          ).toLowerCase();

        const item = payload.location
          ? res.data.find(
              (item) =>
                normalizeLocation(item.GEMEINDE_NAME) ===
                normalizeLocation(payload.location),
            ) || res.data[0]
          : res.data[0];
        this.context.commit("setSingleCityZipcode", payload.zipcode);

        this.context.commit("setCityData", {
          name: item.GEMEINDE_NAME,
          zipcode: payload.zipcode,
          district: item.KREIS_NAME,
        });

        this.context.commit(
          "breadcrumb/setBreadCrumbForCitySite",
          {
            locationInfo: {
              state: item.BUNDESLAND_NAME,
              county: item.KREIS_NAME,
              city: item.GEMEINDE_NAME,
              zipcode: item.POSTLEITZAHL,
              kreisTyp: item.KREIS_TYP,
            },
            basePath: payload.basePath,
          },
          {
            root: true,
          },
        );
      }
    } catch (error) {
      console.error(error);
    }
  }

  @Action
  async fetchStateCountyDataPellets(payload: {
    stateNutsCode: string;
    countyNutsCode: string;
  }) {
    this.context.commit("clearPelletsCountyAssetsPrice");
    try {
      const url = `${process.env.API_ESYOIL}${process.env.COUNTY_PRICE_OVERVIEW}/${payload.stateNutsCode}/${payload.countyNutsCode}?type=PARTNER_PELLET`;

      const result = await axios.get(url);
      const responseData = result.data.data;

      // Pellets case - has loose, pallet, bigbag properties
      const { loose, pallet, bigbag } = responseData;
      this.context.commit("setCurrentPelletsCountyAssetsPrice", {
        loose: loose || null,
        pallet: pallet || null,
        bigbag: bigbag || null,
      });
    } catch (error) {
      console.error(error);
    }
  }

  @Action
  async fetchStateCountyData(payload: {
    stateNutsCode: string;
    countyNutsCode: string;
  }) {
    try {
      const result = await axios.get(
        `${process.env.API_ESYOIL}${process.env.COUNTY_PRICE_OVERVIEW}/${payload.stateNutsCode}/${payload.countyNutsCode}`,
      );
      this.context.commit("setCurrentStateAssetsPrice", result.data.data);
    } catch (error) {
      console.error(error);
    }
  }

  @Action
  async fetchTopUserComments(stateNutsCode: number) {
    this.context.commit("clearUserComments");
    try {
      const result = await axios.get(
        `${process.env.API_ESYOIL}${process.env.FEDERAL_STATE}/${stateNutsCode}/top-user-comments`,
      );
      this.context.commit("restructureAndPushTopComments", result.data.data);
    } catch (err) {
      //
    }
  }

  @Action
  async fetchTopPartners(stateNutsCode: number) {
    this.context.commit("clearPartners");
    try {
      const result = await axios.get(
        `${process.env.API_ESYOIL}${process.env.FEDERAL_STATE}/${stateNutsCode}/top-dealers`,
      );

      if (!result.data?.error) {
        this.context.commit("restructureAndPusTopPartners", result.data.data);
      }
    } catch (err) {
      //
    }
  }

  @Action
  async fetchOilAssets(monthsQUantity: number) {
    try {
      const result = await axios.get(
        `${process.env.API_ESYOIL}${process.env.CHARTS_JAHRESUEBERSICHT}/${monthsQUantity}`,
      );
      this.context.commit("restructureAndPushStateAssets", {
        priceData: result.data.data.data,
        variant: "nation",
      });
    } catch (error) {
      //
    }
  }

  @Action
  async fetchStateArticle(stateName: string) {
    this.context.commit("clearStateArticle");
    try {
      const result = await axios.get(
        `${process.env.API_ESYOIL}${process.env.FEDERAL_STATE_ARTICLE}/${stateName}`,
      );

      this.context.commit("setStateArticle", result.data.data);
    } catch (error) {
      console.error(error);
    }
  }

  @Action
  async fetchPelletsStateArticle(stateName: string) {
    this.context.commit("clearPelletsStateArticle");
    try {
      const result = await axios.get(
        `${process.env.API_ESYOIL}${
          process.env.FEDERAL_STATE_ARTICLE
        }/${encodeURIComponent(stateName)}?type=PARTNER_PELLET`,
      );
      this.context.commit("setPelletsStateArticle", result.data.data);
    } catch (error) {
      console.error(error);
    }
  }

  @Action
  async fetchTopStateCities(stateName: string) {
    this.context.commit("clearOilTopCities");
    try {
      const result = await axios.get(
        `${process.env.API_ESYOIL}${process.env.FEDERAL_STATE_TOP_CITIES}/${stateName}`,
      );
      this.context.commit("setOilTopCities", result.data.data);
    } catch (error) {
      console.error(error);
    }
  }

  @Action
  async fetchPelletsTopStateCities(stateName: string) {
    this.context.commit("clearPelletsTopCities");

    try {
      const result = await axios.get(
        `${process.env.API_ESYOIL}${
          process.env.FEDERAL_STATE_TOP_CITIES
        }/${encodeURIComponent(stateName)}?type=PARTNER_PELLET`,
      );
      const pelletsData = result.data.data;

      this.context.commit("setPelletsTopCities", {
        loose: pelletsData.loose || [],
        pallet: pelletsData.pallet || [],
        bigbag: pelletsData.bigbag || [],
      });
    } catch (error) {
      console.error(error);
    }
  }

  @Action
  async fetchCountyLinks(payload: {
    statesNutsCode: string;
    partnerType?: PartnerType;
  }) {
    this.context.commit("clearCountyLinks");

    let res: any;
    if (payload.partnerType) {
      res = await api.geographyV1RegionNutsStateNutsGet(
        payload.statesNutsCode,
        payload.partnerType,
      );
    } else {
      res = await api.geographyV1RegionNutsStateNutsGet(payload.statesNutsCode);
    }

    const frontRes = res.data.map((item) => {
      return {
        name: item.KREIS_NAME,
        link: encodePlaceNameSegment(item.KREIS_NAME),
        kreisNuts: item.KREIS_NUTSCODE,
      };
    });

    this.context.commit("setCountyLinks", frontRes);
  }

  @Action
  async fetchCurrentStateOilPrice(stateNutsCode) {
    this.context.commit("clearCurrentStateAssetsPrice");
    try {
      const result = await axios.get(
        `${process.env.API_ESYOIL}${process.env.FEDERAL_STATE}/${stateNutsCode}/priceoverview`,
      );
      this.context.commit("setCurrentStateAssetsPrice", result.data.data);
    } catch (error) {
      console.error(error);
    }
  }

  @Action
  async fetchDistrictCities(
    payload: FetchDistrictCitiesPayload,
  ): Promise<void> {
    try {
      const response = (await api.geographyV1StateStateNutsDistrictCitiesGet(
        payload.stateNutsCode,
        payload.partnerType,
      )) as unknown as AxiosResponse<DistrictCityResponse[]>;

      const mappedCities = response.data.map((item) => ({
        name: item.districtCity,
        ...(payload.includeLinkField && {
          link: encodePlaceNameSegment(item.districtCity),
        }),
      }));

      this.context.commit("setDistrictCities", {
        cities: mappedCities,
        currentDistrictCity: payload.currentDistrictCity,
        shouldFilter: payload.shouldFilter,
      });
    } catch (error) {
      console.error("Error fetching district cities:", error);
    }
  }

  @Action
  async fetchDistrictCityZipcodes(payload: {
    districtCity: string;
    urlPrefix: string;
    partnerType?: PartnerType;
  }): Promise<void> {
    try {
      const res = await api.geographyV1GetOtherZipcodesPlaceZipcodeGet(
        payload.districtCity,
        "",
        payload.partnerType,
      );

      const zipcodes = res.data as unknown as string[];

      const urlPrefix = payload.urlPrefix as "pelletspreise" | "heizoelpreise";
      const districtCityOtherzipcodes: DistrictCityZipcode[] = zipcodes.map(
        (zipcode) => ({
          name: payload.districtCity,
          link: `/${urlPrefix}/${urlPrefix}-${encodePlaceNameSegment(
            payload.districtCity,
          )}-${zipcode}`,
          zipcode,
        }),
      );

      this.context.commit(
        "setDistrictCityOtherZipcodes",
        districtCityOtherzipcodes,
      );
    } catch (error) {
      console.error("Error fetching district city zipcodes:", error);
    }
  }

  @Action
  async fetchHistoricalPriceData(payload: { zipcode: number; months: string }) {
    this.context.commit("clearHistoricalPriceData");

    try {
      const result = await axios.get(
        `${process.env.API_ESYOIL}${process.env.ZIPCODE_RELATED_PRICES}/${payload.zipcode}/${payload.months}`,
      );

      this.context.commit("setHistoricalPriceData", result.data.data.series);
    } catch (error) {
      console.error(error);
    }
  }

  @Action
  async fetchPelletsHistoricalPriceData(payload: {
    zipcode: number;
    months: string;
  }) {
    this.context.commit("clearPelletsHistoricalPriceData");

    try {
      const result = await axios.get(
        `${process.env.API_ESYOIL}${process.env.ZIPCODE_RELATED_PRICES}/${payload.zipcode}/${payload.months}?type=PARTNER_PELLET`,
      );
      this.context.commit(
        "setPelletHistoricalPriceData",
        result.data.data.series,
      );
    } catch (error) {
      console.error(error);
    }
  }

  @Action
  async fetchCommunityLinks(payload: {
    communityNutsCode: string;
    partnerType?: PartnerType;
  }) {
    this.context.commit("clearCommunityLinks");

    try {
      let res: any;
      if (payload.partnerType) {
        res = await api.geographyV1DistrictRegionDistrictNutsGet(
          payload.communityNutsCode,
          payload.partnerType,
        );
      } else {
        res = await api.geographyV1DistrictRegionDistrictNutsGet(
          payload.communityNutsCode,
        );
      }

      // Group municipalities by their name and their postcodes
      const groupedMunicipalities = res.data.reduce((acc, item) => {
        if (!acc[item.GEMEINDE_NAME]) {
          acc[item.GEMEINDE_NAME] = {
            name: item.GEMEINDE_NAME,
            postcodes: [],
          };
        }
        acc[item.GEMEINDE_NAME].postcodes.push(item.POSTLEITZAHL);
        return acc;
      }, {});

      // Create one entry per postcode for each community
      const frontRes = Object.values(groupedMunicipalities)
        .flatMap((municipality: GroupedCommunity) => {
          const linkPrefix = payload.partnerType
            ? "pelletspreise"
            : "heizoelpreise";

          if (municipality.postcodes.length === 1) {
            return [
              {
                name: municipality.name,
                displayName: municipality.name,
                link: `/${linkPrefix}/${linkPrefix}-${encodePlaceNameSegment(
                  municipality.name,
                )}-${municipality.postcodes[0]}`,
                zipcode: municipality.postcodes[0],
              },
            ];
          } else {
            return municipality.postcodes.map((zipcode) => ({
              name: municipality.name,
              displayName: `${municipality.name} (${zipcode}) `,
              link: `/${linkPrefix}/${linkPrefix}-${encodePlaceNameSegment(
                municipality.name,
              )}-${zipcode}`,
              zipcode,
            }));
          }
        })
        .sort(
          (a, b) =>
            a.name.localeCompare(b.name) || a.zipcode.localeCompare(b.zipcode),
        );

      this.context.commit("setCommunityLinks", frontRes);
    } catch (error) {
      console.error(error);
    }
  }

  @Action
  async fetchBigCityLinks(payload: {
    stateName: string;
    partnerType?: PartnerType;
    urlPrefix: string;
  }) {
    const res: any =
      await api.geographyV1StateOtherCitiesCountByStreetsStateNameLimitGet(
        payload.stateName,
        50,
        payload.partnerType,
      );

    const frontRes = res.data.map((item) => {
      const urlPrefix = payload.urlPrefix as "pelletspreise" | "heizoelpreise";
      return {
        link: `/${urlPrefix}/${urlPrefix}-${encodePlaceNameSegment(item._id)}-${
          item.POSTLEITZAHL
        }`,
        name: item._id,
        zipcode: item.POSTLEITZAHL,
      };
    });
    this.context.commit("setBigCityLinks", frontRes);
  }

  @Action
  async fetchCurrentPelletsStatePrice({ nutsCode }: { nutsCode: string }) {
    this.context.commit("clearPelletsStateAssetsPrice");
    const url = `${process.env.API_ESYOIL}${process.env.FEDERAL_STATE}/${nutsCode}/priceoverview?type=PARTNER_PELLET`;

    try {
      const result = await axios.get(url);
      const responseData = result.data.data;

      // Pellets case - has loose, pallet, bigbag properties
      const { loose, pallet, bigbag } = responseData;

      this.context.commit("setCurrentPelletsStateAssetsPrice", {
        loose: loose || null,
        pallet: pallet || null,
        bigbag: bigbag || null,
      });
    } catch (error) {
      console.error(error);
    }
  }

  @Action
  async fetchStateOilPrices(payload: {
    stateNutsCode: string;
    monthsQuantity: string;
  }) {
    try {
      const result = await axios.get(
        `${process.env.API_ESYOIL}${process.env.FEDERAL_STATE_CHART}/${payload.stateNutsCode}/${payload.monthsQuantity}`,
      );
      this.context.commit("restructureAndPushStateAssets", {
        priceData: result.data.data.data,
        variant: "state",
        addLabels: true,
      });
    } catch (error) {
      console.error(error);
    }
  }

  @Action
  async fetchHistoryTableData(zipcode: string) {
    try {
      const result = await axios({
        method: "get",
        url: `${process.env.API_ESYOIL}${process.env.HISTORY_TABLE}/${zipcode}/2000/3000/1000`,
        headers: {
          accept: "*/*",
        },
      });
      this.context.commit("setHistoryTableData", result.data.data);
    } catch (error) {
      console.error(error.response.data);
    }
  }

  @Action
  async fetchPartnersAndRatingyByZipcode(zipcode) {
    const exist: any = await api.geographyV1LocationZipcodeExistGet(zipcode);

    const zip = exist.data ? zipcode : "00000";

    this.context.commit("clearPartners");
    this.context.commit("clearUserComments");
    const result = await axios.get(
      `${process.env.API_ESYOIL}${process.env.PARTNERS_AND_RATIUNGS}/${zip}`,
    );

    this.context.commit("restructurePushPartnersAndRatings", result.data.data);
    this.context.commit(
      "restructureAndPushTopComments",
      result.data.data.userRatings,
    );
  }

  @Action
  async fetchCountyIdentData(locationData: {
    stateName: string;
    countyName: string;
    partnerType?: PartnerType;
  }) {
    try {
      const res: any = await api.geographyV1RegionNameStateNameDistrictNameGet(
        locationData.stateName,
        locationData.countyName,
        locationData.partnerType,
      );

      const data = res.data ? res.data : res;

      if (data.meta?.KREIS_NUTSCODE && data.meta?.KREIS_NAME) {
        this.context.commit("setCountyInfo", {
          nutsCode: data.meta.KREIS_NUTSCODE,
          name: data.meta.KREIS_NAME,
        });
      }
    } catch (error) {
      console.error(error);
    }
  }

  @Action
  async fetchOtherZipcodes(payload: {
    location: string;
    zipcode: string;
    partnerType?: PartnerType;
  }) {
    if (payload.location && payload.zipcode) {
      try {
        const result = (await api.geographyV1GetOtherZipcodesPlaceZipcodeGet(
          payload.location,
          payload.zipcode,
          payload.partnerType,
        )) as unknown as AxiosResponse<string[]>;

        if (result.data) {
          this.context.commit("setOtherZipcodes", result.data);
        }
      } catch (error) {
        console.error(error.response.data);
      }
    }
  }

  @Action
  async fetchCityZipcodes(cityName: string) {
    this.context.commit("resetCityRequestData");
    const res: any = await api.geographyV1GetZipcodesByPlaceNameStringGet(
      cityName,
    );
    if (res.data && res.data.length > 0) {
      const item = cityName
        ? res.data.find(
            (item) =>
              item.GEMEINDE_NAME.toLowerCase() ===
              decodeURIComponent(cityName.toLowerCase()),
          ) || res.data[0]
        : res.data[0];

      this.context.commit("setCityData", {
        name: item.GEMEINDE_NAME,
        zipcode: item._id,
        district: item.KREIS_NAME,
      });
      this.context.commit("setSingleCityZipcode", res.data[0]._id);

      this.context.commit(
        "breadcrumb/setBreadCrumbForCitySite",
        {
          locationInfo: {
            state: item.BUNDESLAND_NAME,
            county: item.KREIS_NAME,
            city: item.GEMEINDE_NAME,
            zipcode: item.POSTLEITZAHL,
          },
        },
        {
          root: true,
        },
      );
    }
  }

  @Action
  async fetchPelletsCountyDistrictChartData(payload: {
    countyNutsCode: string;
    monthsQuantity: PelletsChartMonths;
  }) {
    const months = coercePelletsChartMonths(payload.monthsQuantity);
    const cacheKey = `county:${payload.countyNutsCode}:${months}`;
    await fetchPelletsChartWithCache(this.context, {
      url: `${process.env.API_ESYOIL}${process.env.COUNTY_CHART}/${payload.countyNutsCode}/${months}?type=PARTNER_PELLET`,
      cacheKey,
      getCached: () => this.pelletsChartDataCache[cacheKey],
      getPendingKey: () => this.pendingPelletsCountyChartKey,
      setPendingMutation: "setPendingPelletsCountyChartKey",
      loadCachedMutation: "loadCachedPelletsChartData",
      setDataMutation: "setPelletsChartData",
      logScope: "federalstates/fetchPelletsCountyDistrictChartData",
    });
  }
}
